import type { ReactNode } from 'react';
import { Spinner } from '@/components/ui/Spinner';

/**
 * Per-tile colour, kept as whole class strings rather than composed from a tone
 * name — Tailwind only ships classes it can see literally, so
 * `border-${tone}-200` would compile to nothing.
 */
const STAT_TONES = {
  slate:   { box: 'border-slate-200 bg-slate-50',     label: 'text-slate-600',   value: 'text-slate-900',   hint: 'text-slate-500',   spin: 'text-slate-400' },
  emerald: { box: 'border-emerald-200 bg-emerald-50', label: 'text-emerald-800', value: 'text-emerald-900', hint: 'text-emerald-700', spin: 'text-emerald-600' },
  sky:     { box: 'border-sky-200 bg-sky-50',         label: 'text-sky-800',     value: 'text-sky-900',     hint: 'text-sky-700',     spin: 'text-sky-600' },
  amber:   { box: 'border-amber-200 bg-amber-50',     label: 'text-amber-900',   value: 'text-amber-900',   hint: 'text-amber-700',   spin: 'text-amber-600' },
} as const;

export type StatTileTone = keyof typeof STAT_TONES;

export interface StatTileProps {
  label: string;
  /** Already formatted for display — this component does no number formatting. */
  value: string;
  hint: ReactNode;
  tone: StatTileTone;
  loading: boolean;
  failed: boolean;
}

/**
 * One figure on a list page's header.
 *
 * ALWAYS RENDERED, which is the point of it. The payments tiles used to sit
 * behind `paymentStats != null`, so the whole row appeared and disappeared as
 * the query settled — the table jumped under the cursor on every filter change,
 * and the row vanished entirely whenever /payments/stats failed.
 *
 * Three states, and the third is the one that matters:
 *   loading  a spinner where the value goes, in a fixed-height row so the tile
 *            does not change size when the number lands
 *   failed   an em dash. NOT $0.00 — a zero is indistinguishable from a real
 *            figure, and that endpoint does fail in practice. Showing a
 *            plausible wrong number is worse than showing an obviously absent
 *            one, so the caller may pass whatever its `?? 0` fallback produced
 *            and this will refuse to render it.
 *   settled  the value
 *
 * Deliberately NOT components/ui/StatCard: that one is a different design
 * (Card-backed, uppercase tracked label, trend chips, container queries) used
 * by App Orders and the dashboards. This is the flat tinted-box tile the
 * payments header already had, kept as-is so adding a loading state did not
 * quietly restyle the page.
 */
export function StatTile({ label, value, hint, tone, loading, failed }: StatTileProps) {
  const t = STAT_TONES[tone];

  return (
    <div className={`rounded-xl border px-4 py-3 ${t.box}`}>
      <span className={`text-sm font-medium ${t.label}`}>{label}</span>
      <div className={`mt-1 flex h-7 items-center text-xl font-semibold ${t.value}`}>
        {loading ? (
          <Spinner size="sm" className={t.spin} label={`Loading ${label}`} />
        ) : failed ? (
          <span className="text-slate-400" title="Could not load this figure">—</span>
        ) : (
          value
        )}
      </div>
      <p className={`mt-0.5 text-xs ${t.hint}`}>
        {loading || failed ? <span className="opacity-60">{hint}</span> : hint}
      </p>
    </div>
  );
}
